import React, { useEffect, useId, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import MediaWaveWordmark from "./MediaWaveWordmark";
import { AiPlaygroundWordmark } from "./AiPlaygroundWordmark";
import { samePageHashHref, scrollToElementByHash } from "../utils/scrollToHash";
import "./Header.css";

interface HeaderProps {
  onTicketClick?: () => void;
  /** Optional right-side content (e.g. media account / sign-in). */
  trailingSlot?: React.ReactNode;
  variant?: "default" | "ai";
  logoText?: string;
  /** When set, renders this image as the wordmark (e.g. AI page SVG logos). */
  logoSrc?: string;
  logoTo?: string;
}

type NavLinkItem = {
  label: string;
  to: string;
  external?: boolean;
};

type NavDropdown = {
  id: string;
  label: string;
  paths: string[];
  links: NavLinkItem[];
};

const NAV_DROPDOWNS: NavDropdown[] = [
  {
    id: "events",
    label: "Events",
    paths: ["/xrp-australia", "/xrp-london", "/events", "/event", "/ledger-lounge"],
    links: [
      { label: "Sydney 2027", to: "/xrp-australia" },
      { label: "London 2027", to: "/xrp-london" },
      { label: "Past events", to: "/events" },
    ],
  },
  {
    id: "company",
    label: "Company",
    paths: ["/team", "/careers"],
    links: [
      { label: "Team", to: "/team" },
      { label: "Careers", to: "/careers" },
    ],
  },
  {
    id: "resources",
    label: "Resources",
    paths: ["/resources", "/blog", "/gallery", "/gallery_resources", "/branding", "/faq"],
    links: [
      { label: "Blog", to: "/blog" },
      { label: "Gallery", to: "/gallery" },
      { label: "Brand assets", to: "/branding" },
      { label: "FAQ", to: "/faq" },
    ],
  },
];

const Header: React.FC<HeaderProps> = ({ trailingSlot, variant = "default", logoText, logoSrc, logoTo }) => {
  const location = useLocation();
  const baseId = useId();
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const programsPaths: string[] = [];
  const eventsPaths = ["/events", "/sydney2026", "/event", "/ledger-lounge"];
  const flagshipPaths = ["/xrp-australia", "/xrp-london"];
  const aboutPaths = ["/about", "/about-us"];

  const isPathActive = (paths: string[]) =>
    paths.some((path) => location.pathname === path || location.pathname.startsWith(path + "/"));

  const isLinkActive = (to: string) => {
    const [pathOnly] = to.split("#");
    if (pathOnly === "/") return location.pathname === "/";
    return location.pathname === pathOnly || location.pathname.startsWith(pathOnly + "/");
  };

  const isScrolled = true;
  const isVisible = true;

  const isBlogPage =
    location.pathname === "/blog" ||
    location.pathname.startsWith("/blog/") ||
    location.pathname === "/media" ||
    location.pathname.startsWith("/media/") ||
    location.pathname === "/resources" ||
    location.pathname === "/gallery" ||
    location.pathname === "/gallery_resources" ||
    location.pathname === "/branding";
  const isTermsPage = location.pathname === "/terms";
  const isDisclaimerPage = location.pathname === "/disclaimer";
  const isTeamPage =
    location.pathname === "/team" ||
    location.pathname === "/partners" ||
    location.pathname === "/about" ||
    location.pathname === "/about-us";
  const isHomePage = location.pathname === "/" || location.pathname === "/home";
  const isEventsMainPage = location.pathname === "/events";
  const isMediaPages = location.pathname === "/media" || location.pathname.startsWith("/media/");
  const isProgramsOrEventsPage =
    isPathActive(programsPaths) ||
    isPathActive(flagshipPaths) ||
    (isPathActive(eventsPaths) && !isEventsMainPage);
  const useLightTopBar = isHomePage || isEventsMainPage;
  const headerLogoTo = logoTo ?? (isMediaPages ? "/media" : "/");
  const useLogoImage = Boolean(logoSrc);
  const useAiPlaygroundWordmark = variant === "ai" && logoText === "AI Fest 2026" && !useLogoImage;
  const useTextLogo = Boolean(logoText) && !useLogoImage && !useAiPlaygroundWordmark;
  const aiHeaderNavItems = [
    { id: "venue", label: "Venue" },
    { id: "agenda", label: "Agenda" },
    { id: "speakers", label: "Speakers" },
    { id: "hackathon", label: "Hackathon" },
    { id: "event-details", label: "Event Details" },
  ] as const;

  useEffect(() => {
    setOpenMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!openMenu) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openMenu]);

  return (
    <header
      className={`header header--pills ${isScrolled ? "scrolled" : ""} ${isVisible ? "fade-in" : ""} ${isBlogPage || isTermsPage || isDisclaimerPage || isTeamPage ? "blog-header" : ""} ${useLightTopBar ? "home-header" : ""} ${isHomePage ? "header--home" : ""} ${isProgramsOrEventsPage ? "programs-events-header" : ""} ${isMediaPages ? "header--media" : ""} ${variant === "ai" ? "header--ai" : ""}`}
    >
      <div className="header-content">
        <Link to={headerLogoTo} className="header-brand-pill" aria-label="Wave of Innovation home">
          {useLogoImage ? (
            <img
              src={logoSrc}
              alt={logoText ?? "Wave of Innovation AI Conference"}
              className="header-logo header-logo--ai"
            />
          ) : useAiPlaygroundWordmark ? (
            <AiPlaygroundWordmark className="header-logo-text" />
          ) : useTextLogo ? (
            <span className="header-logo-text">
              <span className="header-logo-text-equals">{logoText?.charAt(0) ?? ""}</span>
              <span className="header-logo-text-main">{logoText?.slice(1) ?? ""}</span>
            </span>
          ) : isMediaPages ? (
            <span className="header-logo header-logo--media header-logo--wave-wordmark">
              <MediaWaveWordmark surface="dark" />
            </span>
          ) : (
            <img
              src="/woi_logos/woi+X_logo_light_bg.svg"
              alt="Wave of Innovation"
              className="header-brand-mark"
              width={3750}
              height={975}
              decoding="async"
            />
          )}
        </Link>

        <div className="header-actions">
          <nav id="primary-navigation" className="header-nav header-nav-pill">
            {variant === "ai" ? (
              <ul className="header-nav-list">
                {aiHeaderNavItems.map((item) => (
                  <li key={item.id} className="header-nav-item">
                    <a href={`#${item.id}`} className="header-nav-link">
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="header-nav-list">
                {NAV_DROPDOWNS.map((menu) => {
                  const menuId = `${baseId}-${menu.id}`;
                  const isOpen = openMenu === menu.id;
                  const isActive = isPathActive(menu.paths);
                  return (
                    <li
                      key={menu.id}
                      className={`header-nav-item header-nav-item--dropdown${isOpen ? " is-open" : ""}${isActive ? " is-active" : ""}`}
                      onMouseEnter={() => setOpenMenu(menu.id)}
                      onMouseLeave={() => setOpenMenu((current) => (current === menu.id ? null : current))}
                      onFocus={() => setOpenMenu(menu.id)}
                      onBlur={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                          setOpenMenu((current) => (current === menu.id ? null : current));
                        }
                      }}
                    >
                      <button
                        type="button"
                        className={`header-nav-trigger${isActive ? " active" : ""}`}
                        aria-expanded={isOpen}
                        aria-controls={menuId}
                        onClick={() => setOpenMenu((current) => (current === menu.id ? null : menu.id))}
                      >
                        {menu.label}
                      </button>
                      <ul id={menuId} className="header-dropdown" hidden={!isOpen}>
                        {menu.links.map((link) => (
                          <li key={link.to}>
                            <Link
                              to={link.to}
                              className={`header-dropdown-link${isLinkActive(link.to) ? " active" : ""}`}
                              onClick={() => setOpenMenu(null)}
                            >
                              <span>{link.label}</span>
                              {link.external ? (
                                <span className="header-dropdown-external" aria-hidden="true">
                                  ↗
                                </span>
                              ) : null}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
                <li className="header-nav-item">
                  <Link
                    to="/about"
                    className={`header-nav-link${isPathActive(aboutPaths) ? " active" : ""}`}
                  >
                    About
                  </Link>
                </li>
              </ul>
            )}
          </nav>

          {variant !== "ai" ? (
            <a
              href={samePageHashHref(location.pathname, "#footer-register")}
              className="header-register-pill"
              onClick={(e) => {
                e.preventDefault();
                scrollToElementByHash("#footer-register");
              }}
            >
              Register now
              <span aria-hidden="true">→</span>
            </a>
          ) : null}

          {trailingSlot ? <div className="header-trailing">{trailingSlot}</div> : null}
        </div>
      </div>
    </header>
  );
};

export default Header;
